import React, { useCallback, useEffect, useRef, useState } from "react";
import BaseModal from "../BaseModal";
import BaseButton from "../BaseButton";

const BaseConfirmModal = ({
  open,
  onClose,
  onConfirm,
  confirmText = "Confirm",
  cancelText = "Cancel",
  title,
  description,
  isLoading = false,
  confirmButtonColor = "primary",
  type,
  // A notice has nothing to cancel — only the acknowledgement button is shown.
  hideCancel = false,
}) => {
  // The confirm button also disables itself while its own onConfirm is in flight.
  // Callers used to be responsible for this via isLoading, which meant the button
  // stayed clickable whenever a caller passed the wrong mutation's flag or none at
  // all, and a slow request could be fired several times by repeated clicks.
  const [isConfirming, setIsConfirming] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const busy = isLoading || isConfirming;

  const handleConfirm = useCallback(
    async (event) => {
      if (busy || !onConfirm) return;

      setIsConfirming(true);
      try {
        await onConfirm(event);
      } finally {
        // onConfirm usually closes the modal, which unmounts this component.
        if (isMountedRef.current) setIsConfirming(false);
      }
    },
    [busy, onConfirm],
  );

  const renderIcon = () => {
    if (!type) return null;

    if (type === "warning") {
      return <i className="fa-solid fa-triangle-exclamation text-red-500 text-2xl mr-2"></i>;
    } else if (type === "confirm") {
      return <i className="fa-solid fa-circle-check text-green-500 text-2xl mr-2"></i>;
    }
    return null;
  };

  return (
    <BaseModal className="-top-72" open={open} close={onClose}>
      <div className="container">
        {title && (
          <div className="flex items-center mb-2">
            {type && <span className="mr-2">{renderIcon()}</span>}
            <h6 className="text-dark border-bottom pb-1 w-full">{title}</h6>
          </div>
        )}
        {description && <p>{description}</p>}
      </div>

      <div
        className="flex items-center justify-center bg-white py-6 mt-3"
        style={{ boxShadow: "5px 0px 14px 5px rgba(0, 0, 0, 0.07)" }}
      >
        {!hideCancel && (
          <BaseButton
            className="py-[5px] px-2 bg-[#E8E8E8] text-dark"
            fullWidth={false}
            onClick={onClose}
          >
            {cancelText}
          </BaseButton>
        )}
        <BaseButton
          loaderSize="22px"
          disabled={busy}
          isLoading={busy}
          className="py-[5px] px-4 ml-4 min-w-[100px]"
          color={confirmButtonColor}
          fullWidth={false}
          onClick={handleConfirm}
        >
          {confirmText}
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default BaseConfirmModal;
